import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Loader from '../components/common/Loader';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import { apiClient } from '../api/client';
import { useToast } from '../context/ToastContext';
import { STATUS, JOB_TYPES } from '../config/constants';

const oppSchema = z.object({
  title: z.string().min(3, 'Title is required'),
  company_name: z.string().min(2, 'Company name is required'),
  description: z.string().min(10, 'Description required'),
  skills: z.string().optional(),
  eligibility: z.string().optional(),
  salary: z.string().optional(),
  location: z.string().min(2, 'Location is required'),
  employment_type: z.string().min(1, 'Type is required'),
  deadline: z.string().min(1, 'Deadline is required'),
  openings: z.string().or(z.number()).transform(val => Number(val)).refine(val => val > 0, 'Must be greater than 0').optional(),
});

const AdminOpportunities = ({ defaultTab = 'opportunities' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { addToast } = useToast();

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({
    resolver: zodResolver(oppSchema),
    defaultValues: { employment_type: 'Full Time' }
  });

  useEffect(() => {
    setActiveTab(defaultTab);
    fetchData(defaultTab);
  }, [defaultTab]);

  const fetchData = async (tab) => {
    setLoading(true);
    try {
      if (tab === 'opportunities') {
        const res = await apiClient.get('/opportunities');
        setData(res.data || []);
      } else {
        const res = await apiClient.get('/applications');
        setData(res.data || []);
      }
    } catch (err) {
      addToast(`Failed to load ${tab}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchData(tab);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingId(item.id);
      const formattedDate = item.deadline ? new Date(item.deadline).toISOString().split('T')[0] : '';
      reset({ ...item, deadline: formattedDate });
    } else {
      setEditingId(null);
      reset({ employment_type: 'Full Time' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    reset();
  };

  const onSubmit = async (formData) => {
    try {
      if (editingId) {
        await apiClient.put(`/opportunities/${editingId}`, formData);
        addToast('Updated successfully', 'success');
      } else {
        await apiClient.post('/opportunities', formData);
        addToast('Created successfully', 'success');
      }
      handleCloseModal();
      fetchData('opportunities');
    } catch (err) {
      addToast('Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await apiClient.delete(`/opportunities/${id}`);
      addToast('Deleted successfully', 'success');
      setData(data.filter(item => item.id !== id));
    } catch (err) {
      addToast('Failed to delete', 'error');
    }
  };

  const handleStatusChange = async (appId, status) => {
    try {
      await apiClient.put(`/applications/${appId}/status`, { status });
      addToast('Status updated', 'success');
      setData(data.map(item => item.id === appId ? { ...item, status } : item));
    } catch (err) {
      addToast('Failed to update status', 'error');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="admin-page-title">{activeTab === 'opportunities' ? 'Admin Opportunities' : 'All Applications'}</h1>
          <p className="admin-page-sub">
            {activeTab === 'opportunities' ? 'Manage admin-curated job postings.' : 'View all student applications across the platform.'}
          </p>
        </div>
        {activeTab === 'opportunities' && (
          <Button variant="primary" onClick={() => handleOpenModal()}>+ Add Opportunity</Button>
        )}
      </div>

      <div className="card-elevated">
        <div className="tabs mb-6">
          <button 
            className={`tab-btn ${activeTab === 'opportunities' ? 'active' : ''}`}
            onClick={() => handleTabChange('opportunities')}
          >
            Opportunities
          </button>
          <button 
            className={`tab-btn ${activeTab === 'applications' ? 'active' : ''}`}
            onClick={() => handleTabChange('applications')}
          >
            Applications
          </button>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><Loader /></div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No data found.</div>
        ) : activeTab === 'opportunities' ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Job Info</th>
                  <th>Type & Location</th>
                  <th>Stats</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div className="font-semi text-gray-900">{item.title}</div>
                      <div className="text-sm">{item.company_name}</div>
                    </td>
                    <td>
                      <Badge variant="primary-light" className="mb-1">{item.employment_type}</Badge>
                      <div className="text-xs text-gray-500">{item.location}</div>
                    </td>
                    <td className="text-sm text-gray-600">
                      <div>Deadline: {new Date(item.deadline).toLocaleDateString()}</div>
                      {item.applications_count !== undefined && <div>{item.applications_count} Applicants</div>}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => handleOpenModal(item)}>Edit</Button>
                        <Button size="sm" variant="ghost" className="text-danger" onClick={() => handleDelete(item.id)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Opportunity</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(app => (
                  <tr key={app.id}>
                    <td>
                      <div className="font-semi text-gray-900">{app.student?.full_name}</div>
                      <div className="text-xs text-gray-500">{app.student?.college_email}</div>
                    </td>
                    <td>
                      <div className="text-sm font-medium">{app.opportunity?.title}</div>
                      <div className="text-xs text-gray-500">{app.opportunity?.company_name}</div>
                    </td>
                    <td className="text-sm">{new Date(app.created_at).toLocaleDateString()}</td>
                    <td>
                      <select 
                        className="form-input text-xs py-1.5 pl-2 pr-6 h-auto"
                        value={app.status}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                      >
                        {Object.values(STATUS).map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingId ? 'Edit Opportunity' : 'Add Opportunity'}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-2 gap-x-4 mb-4">
            <Input label="Job Title" {...register('title')} error={errors.title?.message} />
            <Input label="Company Name" {...register('company_name')} error={errors.company_name?.message} />
          </div>
          
          <div className="form-group mb-4">
            <label className="form-label">Description</label>
            <textarea className="form-input textarea" {...register('description')} style={{minHeight: '100px'}}></textarea>
            {errors.description && <span className="form-error">{errors.description.message}</span>}
          </div>
          
          <div className="grid grid-2 gap-x-4 mb-4">
            <div className="form-group">
              <label className="form-label">Employment Type</label>
              <select className="form-input select" {...register('employment_type')}>
                {JOB_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <Input label="Location" {...register('location')} error={errors.location?.message} />
          </div>
          
          <div className="grid grid-2 gap-x-4 mb-4">
            <Input label="Required Skills" {...register('skills')} placeholder="Comma separated" />
            <Input label="Salary/Stipend" {...register('salary')} />
          </div>
          
          <div className="grid grid-2 gap-x-4 mb-6">
            <Input label="Application Deadline" type="date" {...register('deadline')} error={errors.deadline?.message} />
            <Input label="Eligibility Criteria" {...register('eligibility')} />
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button type="button" variant="ghost" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={isSubmitting}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminOpportunities;
